import { useEffect, useRef, useState } from "react"
import { MEDIA_QUERIES } from "@/constants/ui"
import { useVoiceLevels, type VoiceLevels } from "@/hooks/use-voice-levels"
import type { VoiceConnectionStatus } from "@/types/voice-chat-rtc"
import { cn } from "cn"

import { rippleAudioLines } from "@/lib/audio-lines-ripple"
import { useAppSelector } from "@/lib/storage/hooks"
import {
  isVoiceConnecting,
  selectVoiceOpenaiCallId,
} from "@/lib/storage/voice-chat-slice"
import IconAudioLines from "@/components/icons/audio-lines"

// Grow the halo out from behind the orb over this long before amplitude
// tracking takes over, so it swells into a ring instead of snapping to size.
const HALO_ENTER_MS = 300

// Amplitude→scale mapping. The halo hides behind the full-size disc, so only
// its overhang past the disc edge reads as motion — it needs a bigger factor
// than the disc, which moves its entire silhouette, or user speech visually
// drowns out the assistant. The audible base keeps a ring visible between
// phrases (at scale 1 ~8px of the size-28 halo clears the size-24 disc).
const HALO_AUDIBLE_BASE_SCALE = 1.05
// `mini` is deliberately the least reactive: the pill sits over a sheet the user
// is reading, so its orb should register as alive without pulling the eye off
// the page. Scale factors are relative, so mini also starts from a smaller orb —
// the two together keep its swing to a couple of pixels.
const MIC_AMPLITUDE_FACTOR = { full: 0.3, compact: 0.25, mini: 0.14 }

// The live flourish: the orb dips and overshoots while the glyph's waveform
// ripples — the same ripple the voice-mode button plays on hover, so the two
// moments share one motif. Driven through the Web Animations API so a one-shot
// that has started always finishes with nothing to track in React, and the pop
// animates the individual `scale` property, which composes with the orb's
// mic-driven inline `transform` instead of replacing it (the halo relies on the
// same composition).
const LIVE_POP_MS = 560
const LIVE_POP_EASING = "cubic-bezier(0.34, 1.56, 0.64, 1)"

function playLiveFlourish(orb: HTMLElement) {
  if (
    typeof orb.animate !== "function" ||
    window.matchMedia(MEDIA_QUERIES.reducedMotion).matches
  ) {
    return
  }
  orb.animate(
    [{ scale: "0.9" }, { scale: "1.07", offset: 0.45 }, { scale: "1" }],
    { duration: LIVE_POP_MS, easing: LIVE_POP_EASING }
  )
  rippleAudioLines(orb)
}

export type VoiceOrbSize = keyof typeof MIC_AMPLITUDE_FACTOR

// Geometry per size. `full` and `compact` share one 96px layout footprint and
// differ only by a transform, so the orb shrinks in place about its center
// instead of sliding toward whichever edge the parent anchors the footprint to;
// the ring and halo (and the compact disc, inset from the footprint edge)
// diverge from the box via transforms only.
//
// `mini` is a genuinely smaller orb rather than a scaled-down one — the docked
// pill's row has no 80px to give, and a transformed box would reserve it.
const GEOMETRY: Record<
  VoiceOrbSize,
  {
    box: string
    disc: string
    // Disc colors: `bg` is the resting fill (the connecting/error fills
    // override it), `text` the glyph in every state. Theme-aware — every host
    // (the overlay, the docked pill) paints a themed surface.
    tone: { bg: string; text: string }
    // The assistant halo — how every size shows the assistant speaking.
    // `gradient` is theme-aware: every host (the overlay, the docked pill)
    // paints a themed surface.
    halo: { size: string; gradient: string; amplitudeFactor: number }
    ring?: string
    icon: string
  }
> = {
  full: {
    box: "size-24",
    disc: "size-24",
    tone: { bg: "bg-foreground", text: "text-background" },
    halo: {
      // Overhang past the disc is the only part of the halo that reads, so it
      // is sized well past the disc rather than proportionally with it. The
      // ceiling is `compact`, whose peak (size × the audible base + amplitude ×
      // the 1.06 breathing keyframe, then the box's 0.7) has to stay inside the
      // 24px the orb region pads above the caption — at size-28 it reaches
      // ~22px, and a wider halo would wash over the first caption line.
      size: "size-28",
      gradient: "var(--voice-halo-gradient)",
      amplitudeFactor: 0.32,
    },
    ring: "size-34",
    icon: "text-4xl",
  },
  compact: {
    box: "size-24 scale-[0.7]",
    disc: "size-24",
    tone: { bg: "bg-foreground", text: "text-background" },
    halo: {
      size: "size-28",
      gradient: "var(--voice-halo-gradient)",
      amplitudeFactor: 0.28,
    },
    ring: "size-34",
    icon: "text-4xl",
  },
  // No connecting ring: the ping is the loudest thing the orb does, and at pill
  // scale it would swamp the sheet the pill sits over. The disc's grey pulse
  // stands in for it here — the one size where the disc moves while
  // connecting, precisely because nothing else can.
  //
  // Disc and halo are sized together against the mini-player's card, which
  // clips the halo (see voice-mini-player.tsx). That card is as tall as the
  // taller of the orb and the two text lines, plus 24px of padding — so a
  // bigger disc buys NO extra halo room, it just takes the card up with it.
  // The orb is what sets that height at both sizes: 40px against a 36px text
  // block on a phone, 48px against 40px from `sm` up, flooring the card at
  // 64px and 72px.
  //
  // Peak halo diameter is size × (HALO_AUDIBLE_BASE_SCALE + amplitudeFactor) ×
  // the 1.06 of the breathing keyframe: 52.7px inside 64, and 62.3px inside 72,
  // leaving ~5.6px and ~4.9px of card above and below. That gap is the point: a
  // halo that reaches the edges reads as a colour block filling the pill rather
  // than a ring around an orb. Raising either number without re-checking that
  // product spends the gap, and past it the card clips a hard-edged circle into
  // a flat chord.
  //
  // Both stops are class strings rather than a second GEOMETRY entry because
  // nothing else about the pill's orb changes with the breakpoint — the
  // amplitude factors are multipliers, so they hold at either size.
  //
  // The ring is thinner here than on the overlay, so it trades width for swing:
  // a bigger amplitudeFactor than `full` relative to its span, since 3px of
  // resting overhang needs the movement to be seen at all.
  mini: {
    box: "size-10 sm:size-12",
    disc: "size-10 sm:size-12",
    tone: { bg: "bg-foreground", text: "text-background" },
    halo: {
      size: "size-11 sm:size-13",
      gradient: "var(--voice-halo-gradient)",
      amplitudeFactor: 0.08,
    },
    // Half the disc at both stops. It used to be 20px on a 36px disc, which
    // ran well over the proportion the bigger sizes keep; growing the disc
    // under it was the correction, and the wider card keeps the same ratio
    // rather than spending it again.
    icon: "text-xl sm:text-2xl",
  },
}

// Centered voice visualization with three parts:
// 1. connecting ring — a thin ring pings outward while the session starts up,
//    and is the ONLY thing moving until the session's first sound.
// 2. main orb — grey and completely still until the session's first sound,
//    then black, arriving with one pop of the orb and a ripple across the
//    glyph's bars (the live flourish); from that same moment on, fluctuates
//    with the mic level while the user is speaking. It is deliberately inert
//    before then: a disc answering the mic while the session still can't hear
//    it reads as "I'm listening" over dead air.
// 3. lighter opaque halo — fluctuates only while the assistant is speaking,
//    driven by real playback metering when the browser provides it and by a
//    synthetic pulse otherwise.
//
// `size` is one axis with three stops: `full` and `compact` are the overlay's
// hero and docked poses, `mini` is the docked mini-player's pill chip — the same
// orb, smaller and calmer (see GEOMETRY).
//
// Fed by props, with `VoiceChatOrb` below binding them to the app store for
// voice mode. A host whose voice session is not the app store's (the bounded
// interview) meters its own transport and renders this directly, so the rules
// that make the orb voice mode's — what counts as connecting, when the halo
// shows, when the flourish plays — are stated once, here.
export function VoiceOrb({
  status,
  size,
  levels,
  callId,
}: {
  status: VoiceConnectionStatus
  size: VoiceOrbSize
  levels: VoiceLevels
  // The session's call id, written only at `connected` and `reconnected`; a
  // change since this orb mounted is the edge the live flourish plays on.
  callId: string | null
}) {
  const geometry = GEOMETRY[size]
  const tone = geometry.tone
  // One definition of connecting for every surface (see isVoiceConnecting), so
  // the ring stops and the disc darkens on the same `connected` that sounds the
  // start cue — the orb goes live as the customer hears the line open. From the
  // prop rather than the store: teardown returns the slice to the same "idle" a
  // session about to start reads as, and a host whose orb outlives its session
  // (the interview's sits above the completion pills) has to be able to hand it
  // a resting status instead.
  const connecting = isVoiceConnecting(status)
  const speaking = status === "assistant_speaking"
  const error = status === "error"
  const { audible, live } = levels

  // The levels move at up to 60Hz and nothing else here does, so they bypass
  // React: each sample writes two custom properties on the box, and the halo's
  // and disc's inline styles read them through calc(). Render runs only when
  // something discrete changes — status, audibility, the grow-in latch.
  const boxRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const box = boxRef.current
    if (!box) {
      return
    }
    const write = () => {
      const { assistantAmplitude, mic } = live.get()
      box.style.setProperty("--voice-amp", `${assistantAmplitude}`)
      box.style.setProperty("--voice-mic", `${mic}`)
    }
    write()
    return live.subscribe(write)
  }, [live])

  // Latches once the halo has grown in; cleanup resets it so the grow-in
  // replays each speaking turn.
  const [entered, setEntered] = useState(false)
  useEffect(() => {
    if (!audible) {
      return
    }
    const timer = setTimeout(() => setEntered(true), HALO_ENTER_MS)
    return () => {
      clearTimeout(timer)
      setEntered(false)
    }
  }, [audible])
  const entering = audible && !entered

  // The live flourish plays as the line opens. The call id is written only by
  // `connected` and `reconnected`, so a change since this orb mounted is that
  // edge: an orb mounted into a running call (the mini-player docking) sees
  // none, a failed connect never sets one, and a rebuild sets a new one.
  const orbRef = useRef<HTMLDivElement>(null)
  const [mountCallId] = useState(callId)
  useEffect(() => {
    if (callId && callId !== mountCallId && orbRef.current) {
      playLiveFlourish(orbRef.current)
    }
  }, [callId, mountCallId])

  // The box is the layout footprint; see GEOMETRY for how each size claims it.
  return (
    <div
      className={cn(
        "relative flex shrink-0 items-center justify-center transition-transform duration-300",
        geometry.box
      )}
      ref={boxRef}
    >
      {/* 1. Connecting ring — overlay sizes only. */}
      {connecting && geometry.ring && (
        <span
          aria-hidden="true"
          className={cn(
            "absolute rounded-full border border-foreground/25 motion-safe:animate-ping",
            geometry.ring
          )}
        />
      )}
      {/* 3. Assistant halo. Always mounted, so the end of audible playback eases
          it out behind the orb (scale < disc/halo hides it under the z-10 disc)
          instead of vanishing on unmount. */}
      <span
        aria-hidden="true"
        className={cn(
          "absolute rounded-full bg-linear-to-br bg-size-[400%_400%] transition-[scale,opacity] ease-out",
          geometry.halo.size,
          // While audible the gradient pans and a breathing swell (keyframed on
          // `transform`) composes under the amplitude-driven `scale`, so the
          // halo stays alive between phrases.
          //
          // Nothing runs at rest, deliberately. `background-position` is not a
          // compositable property, so panning the gradient behind an opacity-0
          // element would dirty style every frame for something invisible by
          // construction — forever, on every orb the app has mounted. Keeping
          // it going buys nothing either: the two animations have different
          // names, so the gradient's phase restarts at zero when a turn begins
          // whatever was running before it.
          audible && "motion-safe:animate-voice-halo",
          audible ? "opacity-100" : "opacity-0",
          // Slow for the grow-in and ease-out; snappy once entered so the
          // halo tracks amplitude.
          audible && !entering ? "duration-75" : "duration-300"
        )}
        style={{
          backgroundImage: geometry.halo.gradient,
          // The individual `scale` property, not `transform`: a running
          // animation would override an inline `transform`, while `scale`
          // composes with the keyframe multiplicatively.
          scale: audible
            ? `calc(${HALO_AUDIBLE_BASE_SCALE} + var(--voice-amp, 0) * ${geometry.halo.amplitudeFactor})`
            : "0.8",
        }}
      />

      {/* 2. Main orb: fluctuates only while the user is speaking. */}
      <div
        className={cn(
          "relative z-10 flex items-center justify-center rounded-full transition-[transform,background-color] duration-180 ease-out",
          geometry.disc,
          tone.text,
          // Error shares the connecting fill: a lost connection is a calm
          // terminal fact, and a red orb read as an alarm (never red — the
          // consumer did nothing wrong).
          error || connecting ? "bg-gray-9" : tone.bg,
          // The pulse is the ring's stand-in, not its companion: where the
          // ring runs, the disc holds perfectly still and the connect motion
          // is the ring alone. Fading a solid disc reads as breathing, and
          // against a ring already scaling outward the two together looked
          // like the orb itself changing size. Only `mini`, which has no room
          // for a ring, still carries it.
          connecting && !geometry.ring && "motion-safe:animate-pulse"
        )}
        ref={orbRef}
        style={
          error
            ? undefined
            : {
                transform: speaking
                  ? "scale(1)"
                  : `scale(calc(1 + var(--voice-mic, 0) * ${MIC_AMPLITUDE_FACTOR[size]}))`,
              }
        }
      >
        <IconAudioLines className={geometry.icon} />
      </div>
    </div>
  )
}

// Voice mode's orb: `VoiceOrb` bound to the app store.
export function VoiceChatOrb({
  status,
  size,
}: {
  status: VoiceConnectionStatus
  size: VoiceOrbSize
}) {
  const levels = useVoiceLevels(status)
  const callId = useAppSelector(selectVoiceOpenaiCallId)
  return (
    <VoiceOrb status={status} size={size} levels={levels} callId={callId} />
  )
}
